import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Plus, Search, Pencil, Trash2 } from 'lucide-react';
import Button from 'components/Button';
import Toast from 'components/Toast';
import RefreshButton from 'components/RefreshButton';
import Pagination from 'components/Pagination';
import { useRoleData } from 'hooks/useRoleData';
import { Role } from 'types/role';
import AddRoleModal from './AddRoleModal';
import EditRoleModal from './EditRoleModal';
import DeleteRoleModal from './DeleteRoleModal';
import styles from './Role.module.scss';

const RolePage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [roleToEdit, setRoleToEdit] = useState<Role | null>(null);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);

  const {
    roles,
    loading,
    reloading,
    error,
    reload,
    addRole,
    updateRole,
    removeRole,
  } = useRoleData();

  const showToast = useCallback(
    (message: string, type: 'success' | 'error') => {
      setToast({ message, type });
    },
    [],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setCurrentPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const filteredRoles = useMemo(() => {
    const searchLower = debouncedSearchQuery.toLowerCase();
    return roles.filter(
      (r) =>
        r.name.toLowerCase().includes(searchLower) ||
        (r.description?.toLowerCase().includes(searchLower) ?? false) ||
        r.permissions.some((p) => p.name.toLowerCase().includes(searchLower)),
    );
  }, [roles, debouncedSearchQuery]);

  const paginatedRoles = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredRoles.slice(startIndex, startIndex + pageSize);
  }, [filteredRoles, currentPage, pageSize]);

  const handleAddSuccess = (role: Role) => {
    addRole(role);
    setIsAddModalOpen(false);
    showToast('Role created successfully', 'success');
  };

  const handleEditSuccess = (role: Role) => {
    updateRole(role);
    setRoleToEdit(null);
    showToast('Role updated successfully', 'success');
  };

  const handleDeleteSuccess = (id: string) => {
    removeRole(id);
    setRoleToDelete(null);
    showToast('Role deleted successfully', 'success');
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Roles</h1>
        <div className={styles.headerButtons}>
          <RefreshButton onClick={reload} loading={reloading} />
          <Button onClick={() => setIsAddModalOpen(true)}>
            <Plus size={18} style={{ marginRight: '0.5rem' }} />
            New Role
          </Button>
        </div>
      </div>

      <div className={styles.filters}>
        <div className={styles.searchContainer}>
          <Search size={18} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search roles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      </div>

      {loading ? (
        <div className={styles.loadingState}>
          <div className={styles.loadingText}>Loading roles...</div>
        </div>
      ) : error ? (
        <div className={styles.errorState}>
          <p>{error}</p>
          <Button variant="outline" onClick={reload}>
            Retry
          </Button>
        </div>
      ) : roles.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No roles found. Create one to get started.</p>
        </div>
      ) : (
        <div className={styles.tableContainer}>
          {reloading && (
            <div className={styles.loadingOverlay}>
              <span>Reloading...</span>
            </div>
          )}
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th className={styles.desktopOnlyCell}>Description</th>
                <th>Permissions</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRoles.map((role) => (
                <tr key={role.id}>
                  <td className={styles.nameCell}>
                    <div>{role.name}</div>
                    {role.description && (
                      <div className={styles.mobileDescription}>
                        {role.description}
                      </div>
                    )}
                  </td>
                  <td
                    className={`${styles.descriptionCell} ${styles.desktopOnlyCell}`}
                  >
                    {role.description || '-'}
                  </td>
                  <td>
                    <div className={styles.permissionsCell}>
                      {/* Full badges (up to 3) shown only on 640px+ */}
                      {role.permissions.slice(0, 3).map((p, index) => (
                        <span
                          key={p.id}
                          className={`${styles.permissionBadge} ${
                            index > 0 ? styles.permissionDesktopOnly : ''
                          }`}
                        >
                          {p.name}
                        </span>
                      ))}

                      {/* Desktop "more" count (for > 3) */}
                      {role.permissions.length > 3 && (
                        <span
                          className={`${styles.permissionBadge} ${styles.permissionDesktopOnly}`}
                        >
                          +{role.permissions.length - 3} more
                        </span>
                      )}

                      {/* Mobile "more" count (for > 1) */}
                      {role.permissions.length > 1 && (
                        <span
                          className={`${styles.permissionBadge} ${styles.permissionTouchOnly}`}
                        >
                          +{role.permissions.length - 1} more
                        </span>
                      )}

                      {role.permissions.length === 0 && '-'}
                    </div>
                  </td>
                  <td className={styles.actionsCell}>
                    <button
                      className={`${styles.actionButton} ${styles.editButton}`}
                      onClick={() => setRoleToEdit(role)}
                      title="Edit Role"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      className={`${styles.actionButton} ${styles.deleteButton}`}
                      onClick={() => setRoleToDelete(role)}
                      title="Delete Role"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {filteredRoles.length > 0 && (
        <div className={styles.paginationWrapper}>
          <Pagination
            currentPage={currentPage}
            totalItems={filteredRoles.length}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
          />
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <AddRoleModal
        show={isAddModalOpen}
        onHide={() => setIsAddModalOpen(false)}
        onSuccess={handleAddSuccess}
        onError={(msg) => showToast(msg, 'error')}
      />

      <EditRoleModal
        show={!!roleToEdit}
        onHide={() => setRoleToEdit(null)}
        role={roleToEdit}
        onSuccess={handleEditSuccess}
        onError={(msg) => showToast(msg, 'error')}
      />

      <DeleteRoleModal
        show={!!roleToDelete}
        onHide={() => setRoleToDelete(null)}
        role={roleToDelete}
        onSuccess={handleDeleteSuccess}
        onError={(msg) => showToast(msg, 'error')}
      />
    </div>
  );
};

export default RolePage;
